import { PageHeader } from "@/components/common/PageHeader";
import { Reveal } from "@/components/common/Reveal";

const sections = {
  "Privacy Policy": [
    {
      title: "1. About this policy",
      body: [
        "Success Crest Consulting (\"SCC\", \"we\", \"us\", or \"our\") respects your privacy. This Privacy Policy explains how we collect, use, store, and protect personal information when you use our website and services."
      ]
    },
    {
      title: "2. Information we collect",
      body: [
        "Depending on how you use our website, we may collect your name, email address, phone number, professional information, employment preferences, resume or CV, and information you provide through enquiry or contact forms.",
        "We may also receive technical information such as your IP address, browser type, device information, and website usage information."
      ]
    },
    {
      title: "3. How we use your information",
      body: [
        "We use personal information to respond to enquiries, provide recruitment and consulting services, review candidate profiles and resumes, communicate with candidates and employers, coordinate opportunities, maintain business records, improve our website and services, and protect the security of our systems."
      ]
    },
    {
      title: "4. Resume and candidate information",
      body: [
        "If you submit a resume or candidate information, we may use it to assess your profile for relevant opportunities and recruitment services.",
        "We do not promise employment, placement, interviews, or selection as a result of submitting information through our website."
      ]
    },
    {
      title: "5. Storage and service providers",
      body: [
        "Information submitted through the website may be processed and stored using third-party technology and cloud service providers that support our website and business operations, including Google services used for business data storage and document management.",
        "We take reasonable measures to protect information against unauthorized access, alteration, disclosure, or destruction."
      ]
    },
    {
      title: "6. Sharing of information",
      body: [
        "We may share relevant information where reasonably necessary to provide our services, including with prospective employers, clients, recruitment partners, service providers, or professional advisers.",
        "We do not sell personal information as a standalone commercial product."
      ]
    },
    {
      title: "7. Data retention",
      body: [
        "We retain personal information only for as long as reasonably necessary for the purposes described in this policy, to provide services, maintain business records, resolve disputes, comply with applicable legal obligations, or protect our legitimate business interests."
      ]
    },
    {
      title: "8. Your choices and requests",
      body: [
        "You may contact us to ask about personal information we hold about you, request correction of inaccurate information, or request deletion where applicable.",
        "Some information may need to be retained where required for legal, security, contractual, or legitimate business purposes."
      ]
    },
    {
      title: "9. Children",
      body: [
        "Our website and services are intended for adults and business users. We do not knowingly request personal information from children."
      ]
    },
    {
      title: "10. Changes to this policy",
      body: [
        "We may update this Privacy Policy from time to time. The updated version will be published on this page with the revised effective date."
      ]
    },
    {
      title: "11. Contact us",
      body: [
        "For privacy-related questions or requests, contact Success Crest Consulting at info@successcrestconsulting.com."
      ]
    }
  ],

  "Terms of Service": [
    {
      title: "1. Use of the website",
      body: [
        "By using the Success Crest Consulting website, you agree to use it lawfully and not to interfere with its operation, security, or availability."
      ]
    },
    {
      title: "2. Consulting and recruitment services",
      body: [
        "Information on this website describes our services and does not constitute a guarantee of employment, hiring, placement, business results, or any particular outcome.",
        "Specific services, deliverables, fees, timelines, and responsibilities may be agreed separately with a client."
      ]
    },
    {
      title: "3. Candidate information",
      body: [
        "Candidates are responsible for ensuring that information submitted to SCC, including resumes and professional details, is accurate and not misleading."
      ]
    },
    {
      title: "4. Intellectual property",
      body: [
        "Unless otherwise stated, the website design, branding, text, graphics, and other content provided by SCC are owned by or licensed to SCC and may not be copied or reused without permission."
      ]
    },
    {
      title: "5. Third-party services",
      body: [
        "The website may rely on third-party platforms and services. SCC is not responsible for interruptions or changes to third-party services outside our reasonable control."
      ]
    },
    {
      title: "6. Changes",
      body: [
        "We may modify website content, services, or these Terms from time to time. Continued use of the website after changes are published constitutes acceptance of the updated Terms."
      ]
    },
    {
      title: "7. Contact",
      body: [
        "For questions about these Terms, contact info@successcrestconsulting.com."
      ]
    }
  ],

  "Cookie Policy": [
    {
      title: "1. What cookies are",
      body: [
        "Cookies are small files or similar technologies that may be stored on your device when you visit a website."
      ]
    },
    {
      title: "2. How we use them",
      body: [
        "Success Crest Consulting may use cookies or similar technologies that are necessary for website functionality, security, performance, analytics, or understanding how visitors use the website."
      ]
    },
    {
      title: "3. Third-party technologies",
      body: [
        "Some website functionality may be provided through third-party services. Those providers may use their own technologies subject to their respective privacy policies."
      ]
    },
    {
      title: "4. Managing cookies",
      body: [
        "Most browsers allow you to control or delete cookies through browser settings. Disabling certain cookies may affect website functionality."
      ]
    },
    {
      title: "5. Changes",
      body: [
        "We may update this Cookie Policy when our website or use of cookies changes."
      ]
    },
    {
      title: "6. Contact",
      body: [
        "For questions about cookies or this policy, contact info@successcrestconsulting.com."
      ]
    }
  ]
};

export default function LegalPage({ title }) {
  const content = sections[title] || [];

  return (
    <div data-testid="legal-page">
      <PageHeader overline="Legal" title={title} />

      <section className="mx-auto max-w-3xl px-5 py-20 lg:px-8 lg:py-24">
        <Reveal>
          <div className="space-y-10 text-slate-600">
            {content.map((section) => (
              <div key={section.title}>
                <h2 className="mb-3 text-xl font-semibold text-slate-900">
                  {section.title}
                </h2>

                <div className="space-y-3 leading-relaxed">
                  {section.body.map((paragraph) => (
                    <p key={paragraph}>{paragraph}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Reveal>
      </section>
    </div>
  );
}
